import { NgClass, NgStyle } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  SecurityContext,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  featherAlertOctagon,
  featherAlertTriangle,
  featherCheckCircle,
  featherInfo,
  featherX
} from '@ng-icons/feather-icons';

import { animations } from './animations';
import { ToastRef, ToastRefAfterClosed } from './toast-ref';

function isHTMLString(str: string): boolean {
  const htmlRegex = /<[^>]+>/g;
  return htmlRegex.test(str);
}

export type ToastVariant = 'success' | 'danger' | 'warning' | 'info';
export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

@Component({
  selector: 'liv-toast',
  standalone: true,
  imports: [NgClass, NgStyle, NgIconComponent],
  providers: [
    provideIcons({
      featherCheckCircle,
      featherX,
      featherAlertTriangle,
      featherAlertOctagon,
      featherInfo
    })
  ],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  animations
})
export class ToastComponent implements OnInit, OnDestroy {
  readonly #sanitizer = inject(DomSanitizer);

  readonly offset = input<number>(0);
  readonly index = input<number>(0);

  readonly message = input.required<string>();
  readonly className = input<string>('');
  readonly duration = input<number>(3000);
  readonly variant = input<ToastVariant>('warning');
  readonly position = input<ToastPosition>('top-center');
  readonly dismissible = input<boolean>(false);
  readonly autoClose = input<boolean>(true);
  readonly showIcon = input<boolean>(true);
  readonly id = input.required<string>();
  readonly instanceId = input.required<string>();

  readonly ref = input<ToastRef>();

  readonly closed = output<ToastRefAfterClosed>();

  readonly toastState = signal<'open' | 'closed'>('closed');

  readonly offsetFromSibling = computed(() => {
    const stackOffset = this.offset();

    if (stackOffset === 0) {
      return 0;
    }

    if (this.position().includes('bottom')) {
      return stackOffset * -1;
    }

    return stackOffset;
  });

  readonly icon = computed(() => {
    const variant = this.variant();

    const icons = {
      success: 'featherCheckCircle',
      danger: 'featherAlertOctagon',
      warning: 'featherAlertTriangle',
      info: 'featherInfo'
    };

    return icons[variant] || '';
  });

  readonly toastHTML = computed(() => {
    if (isHTMLString(this.message())) {
      return this.#sanitizer.sanitize(SecurityContext.HTML, this.message());
    }
    return this.message();
  });

  readonly toastAnimationState = computed(() => {
    const position = this.position();
    const state = this.toastState();

    if (position.includes('top')) {
      return `${state}-top`;
    } else if (position.includes('bottom')) {
      return `${state}-bottom`;
    }

    return state;
  });

  readonly #dynamicClasses = computed(() => {
    const variant = this.variant();
    const dismissible = this.dismissible();

    const classes = {
      'bg-feedback-success-medium border-feedback-success-light':
        variant === 'success',
      'bg-feedback-warning-pure border-feedback-warning-light':
        variant === 'danger',
      'bg-feedback-helper-pure border-feedback-helper-light':
        variant === 'warning',
      'bg-brand-sextenary-pure border-brand-sextenary-light':
        variant === 'info',
      'pr-4': !dismissible,
      'pr-2': dismissible
    };

    return {
      ...classes,
      [this.className()]: !!this.className()
    };
  });

  readonly containerClasses = computed(() => {
    const position = this.position();

    const classes = {
      'bottom-0': position.includes('bottom'),
      'top-0': position.includes('top'),
      'items-start justify-start': position === 'top-left',
      'items-start justify-center': position === 'top-center',
      'items-start justify-end': position === 'top-right',
      'items-end justify-start': position === 'bottom-left',
      'items-end justify-center': position === 'bottom-center',
      'items-end justify-end': position === 'bottom-right'
    };

    return Object.keys(classes)
      .filter((key) => classes[key])
      .join(' ');
  });

  readonly combinedClasses = computed(() => {
    const dynamicClasses = this.#dynamicClasses();

    const dynamicClassesString = Object.keys(dynamicClasses)
      .filter((key) => dynamicClasses[key])
      .join(' ');

    return `${dynamicClassesString} ${this.className() || ''}`;
  });

  #openTimeout: NodeJS.Timeout | undefined;
  #closeTimeout: NodeJS.Timeout | undefined;
  #closeAnimationTimeout: NodeJS.Timeout | undefined;
  #isDestroyed = false;
  #isClosing = false;

  ngOnInit() {
    this.ref()?.setCloseHandler(() => this.close());

    this.#openTimeout = setTimeout(() => {
      this.toastState.set('open');
    });

    let durationToClose = this.duration();

    const index = this.index();

    if (index > 0) {
      durationToClose = durationToClose + index * 1000;
    }

    if (this.autoClose() && this.duration() > 0) {
      this.#closeTimeout = setTimeout(() => {
        this.close();
      }, durationToClose);
    }
  }

  ngOnDestroy() {
    this.#isDestroyed = true;

    this.ref()?.setCloseHandler(null);

    this.#clearAllTimeouts();
  }

  close(dismissedByAction = false) {
    if (this.#isClosing || this.#isDestroyed) {
      return;
    }

    this.#isClosing = true;
    this.ref()?.notifyClosing();

    this.#clearAllTimeouts();

    this.toastState.set('closed');

    this.#closeAnimationTimeout = setTimeout(() => {
      if (this.#isDestroyed) {
        return;
      }
      this.closed.emit({
        dismissedByAction,
        id: this.id(),
        instanceId: this.instanceId()
      });
    }, 150);
  }

  #clearTimeoutRef(timeout: NodeJS.Timeout | undefined): undefined {
    if (timeout) {
      clearTimeout(timeout);
    }

    return undefined;
  }

  #clearAllTimeouts() {
    this.#openTimeout = this.#clearTimeoutRef(this.#openTimeout);
    this.#closeTimeout = this.#clearTimeoutRef(this.#closeTimeout);
    this.#closeAnimationTimeout = this.#clearTimeoutRef(
      this.#closeAnimationTimeout
    );
  }
}
